import { Close, HighlightOff, Search } from "@mui/icons-material"
import { Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, TextField, Typography, Box, Skeleton, Button, useMediaQuery, Chip, Alert  } from "@mui/material"
import { useEffect, useState } from "react"
import { useFormContext } from 'react-hook-form';
import { useListContext, useTranslate } from "react-admin"
import { TreeViewCheck } from "src/components/uiElements/styledComponents/TreeViewCheck"
import get from 'lodash/get'
import { useQuery } from 'react-query'
import { SplitByCharacterWithPosition } from "src/helper/splitByCharacter"
import EditIcon from '@mui/icons-material/Edit';

const assignIdToValue = (objects, labelField, altLabel = []) => {
  if (Array.isArray(objects)) {
    return objects?.map((obj) => {
      const newObj = {
        id: obj?.id,
        fiscal_data_relation: obj?.fiscal_data_relation,
        label: obj?.label,
        token: obj?.token,
        fantasy_name: obj?.fantasy_name,
        short_name: obj?.short_name,
        long_name: obj?.long_name,
        value: obj?.value,
        children: obj?.children || null,
      }

      // Usar 'get' para acceder a propiedades anidadas
      newObj.label = labelField
        ?.map((field) => get(obj, field))
        .filter((elem) => elem)
        .join(' - ')
      if(!newObj.label && altLabel.length){
        newObj.label = altLabel
          ?.map((field) => get(obj, field))
          .filter((elem) => elem)
          .join(' - ')
      }
      if (newObj.id !== undefined) {
        newObj.value = String(newObj.id) + '__' + newObj.label
      }

      if (newObj.children && newObj.children.length > 0) {
        newObj.children = assignIdToValue(newObj.children, labelField, altLabel)
      } else {
        newObj.children = null
      }

      return newObj
    })
  }
  return null
}

const getId = (value) => {
  if (value && value.length) {
    return value.map((value) => SplitByCharacterWithPosition(value, '__', 0)).join(',')
  }
}

const getDefaultValueTreeView = (data, value) => {
  let valueAux = []
  data.forEach((elem) => {
    if (elem.id === value) {
      valueAux.push(elem.value)
    } else if (elem.children && elem.children.length > 0) {
      valueAux = valueAux.concat(getDefaultValueTreeView(elem.children, value))
    }
  })

  return valueAux
}


export const TreeViewInput = ({
  queryName,
  fieldName,
  modalTitle,
  buttonText,
  labelField,
  customTrigger,
  setCustomtrigger,
  queryFn,
  setResetFilterValues,
  resetValues = false,
  limitOptions = 30,
  queryOptions = {},
  loadingLabel = null,
  altLabel = []
}) => {
  const options = useQuery(queryName, () => queryFn(), queryOptions)
  const translate = useTranslate()
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState([])
  const [currentOption, setCurrentOption] = useState([])
  const [formatedOptions, setFormatedOptions] = useState([])
  const { setValue } = useFormContext()
  const { filterValues } = useListContext()

  useEffect(() => {
    if (open) {
      setFormatedOptions(assignIdToValue(options.data?.slice(0, limitOptions), labelField, altLabel))
      setExpanded([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const getLabel = (value, emptyText) => {
    if (value && value.length) {
      return value.map((value) => SplitByCharacterWithPosition(value, '__', 1)).join(', ')
    }
  
    return emptyText
  }

  useEffect(() => {
    if (!options.isLoading) {
      const nodes = assignIdToValue(options.data?.slice(0, limitOptions), labelField, altLabel)
      const defaultValues = []
      filterValues[fieldName]?.split(',')?.forEach((elem) => {
        const id = parseInt(elem) || null
        if (id) {
          const defaultValue = getDefaultValueTreeView(nodes, id)
          defaultValues.push(defaultValue)
        }
      })
      setCurrentOption(defaultValues.flat())
      setFormatedOptions(nodes)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.isLoading])

  useEffect(() => {
    if (currentOption) {
      setValue(fieldName, getId(currentOption))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOption])

  useEffect(() => {
    if (resetValues) {
      setCurrentOption(null)
      setResetFilterValues(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetValues])

  const onSearch = (value) => {
    if (value.length >= 3) {
      const nodes = assignIdToValue(options.data, labelField, altLabel)
      const [filteredNodes, expandedNodes] = filterTreeView(nodes, value.toLowerCase(), 'label')
      setFormatedOptions(filteredNodes)
      setExpanded(expandedNodes)
    } else {
      // Si la búsqueda es menos de 3 caracteres, muestra solo las opciones seleccionadas o resetea a la vista inicial
      setFormatedOptions(
        currentOption.length
          ? currentOption
          : assignIdToValue(options.data.slice(0, limitOptions), labelField, altLabel)
      )
    }
  }

const filterTreeView = (data, value, labelField) => {
  let expanded = []

  if (value) {
    const dataAux = []
    data.forEach((elem) => {
      const [filteredChildren, expandedChildren] = elem.children
        ? filterTreeView(elem.children, value, labelField)
        : [[], []]
      if (filteredChildren.length > 0) {
        //elem.children = filteredChildren
        dataAux.push(elem)
        expanded = expanded.concat([elem.value, expandedChildren]).flat()
      } else if (elem[labelField]?.toLowerCase()?.includes(value)) {
        dataAux.push(elem)
      }
    })

    return [dataAux, expanded]
  }

  return [data, expanded]
}

const onCloseModal = () => {
    setOpen(false)
    setCustomtrigger(!customTrigger)
  }

  return (
    <>
      <Dialog open={open} maxWidth='md' fullWidth onClose={onCloseModal}>
        <DialogTitle display='flex' justifyContent='space-between'>
          {modalTitle}
          <IconButton size='small' onClick={onCloseModal}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size='small'
            placeholder={translate('ra.message.select_at_least', { num: 3 })}
            style={{ marginBottom: 24 }}
            onChange={(e) => e.target.value?.length >= 3 && onSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          {formatedOptions?.length >= limitOptions && (
            <Typography variant='caption' display='block' style={{ color: 'gray', marginLeft: 10 }}>
              vista previa de resultados ({limitOptions}).
            </Typography>
          )}
          <TreeViewCheck
            checked={currentOption}
            setChecked={setCurrentOption}
            nodes={formatedOptions}
            setExpanded={setExpanded}
            expanded={expanded}
            onlyOne={false}
            noCascade={false}
            activeHistorical={true}
            allNodes={assignIdToValue(options.data, labelField, altLabel)}
          />
        </DialogContent>
      </Dialog>

      <Box width={'100%'}>
        {options.isLoading ? (
          <Box
            sx={{
              bgcolor: 'action.hover',
              borderRadius: 1,
              px: 2,
              py: 1,
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%': {
                  opacity: 1,
                },
                '50%': {
                  opacity: 0.4,
                },
                '100%': {
                  opacity: 1,
                },
              },
            }}
          >
            {loadingLabel ?? translate('ra.action.loading')}
          </Box>
 
        ) : (
          <Button
            sx={{
              border: 'none',
              width: '100%',
              height: '40px',
            }}
            onClick={() => setOpen(true)}
            variant='outlined'
            style={{display:'flex', flexDirection:'row', justifyContent:'space-between'}}
          >
            <span style={{ textWrap: 'nowrap', textOverflow: 'ellipsis', overflowX: 'hidden' }}>
              {getLabel(currentOption, buttonText)}
            </span>
            {(currentOption && currentOption.length > 0) && <EditIcon/>}
          </Button>
        )}
      </Box>
    </>
  )
}